import { useLoaderData, useSearchParams } from 'react-router-dom'
import {
    GetEmployeeNameByUuid,
    GetObjectPermissionLevel,
    ListObjectWithPaginationResponse,
    ListObjectWithPaginationV2,
} from '@/modules/Drive/services'
import { Loading } from 'vnetwork-ui'
import FileGrid from '../FileGrid'
import { extractObjectName } from '../../helpers/helper'
import Header from './Header'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { isValidUUID } from '@/modules/Drive/utils'
import useDriveStore from '@/stores/useDriveStore'
import useAuthStore from '@/stores/useAuthStore'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useInfiniteQueryWithHandler } from '@/hooks/useInfiniteQueryWithHandler'
import { PermissionLevel } from '@/modules/Drive/utils/DrivePermission/permission'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

const FolderExplorer = () => {
    const { fullPath, decodedPath } = useLoaderData() as {
        fullPath: string
        decodedPath: string
    }
    const employee = useAuthStore((state) => state.employee)
    const itemNames = useDriveStore((state) => state.itemNames)
    const appendItemName = useDriveStore((state) => state.appendItemName)
    const folderName = extractObjectName(fullPath || '')
    const [title, setTitle] = useState(folderName)
    const { setIsSelectedMode, setIsSelectedAll, clearSelectedItems } =
        useDriveStore()
    const setCurrentPermission = useDriveStore(
        (state) => state.setCurrentPermission
    )
    const [searchParams] = useSearchParams()

    // Check if user owns this folder (skip API permission check)
    const isOwnFolder = employee?.employee_uuid === decodedPath.split('/')[0]

    // Permission check via API — only when user doesn't own the folder
    const { data: permissionData, isLoading: isPermissionLoading } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.DRIVE_FOLDER_EXPLORER,
                'permission',
                fullPath,
                employee?.employee_id,
            ],
            queryFn: async () => {
                const response = await GetObjectPermissionLevel(
                    fullPath,
                    employee!.employee_id
                )
                return response.result?.share_permission_level ?? null
            },
            enabled: !!employee && !isOwnFolder,
        })

    const permissionLevel = isOwnFolder
        ? PermissionLevel.EDIT
        : (permissionData ?? null)

    useEffect(() => {
        if (permissionLevel) {
            setCurrentPermission(permissionLevel)
        }
    }, [permissionLevel])

    useEffect(() => {
        return () => {
            setIsSelectedMode(false)
            setIsSelectedAll(false)
            clearSelectedItems()
        }
    }, [])

    useEffect(() => {
        const fetchTitle = async () => {
            if (isValidUUID(folderName)) {
                if (itemNames[folderName]) {
                    setTitle(itemNames[folderName])
                } else {
                    const result = await GetEmployeeNameByUuid(folderName)
                    if (result.result) {
                        setTitle(result.result)
                        appendItemName(folderName, result.result)
                    }
                }
            } else setTitle(folderName)
        }
        fetchTitle()
    }, [folderName, itemNames])

    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQueryWithHandler({
        queryKey: [
            QUERY_KEYS.DRIVE_FOLDER_EXPLORER,
            decodedPath,
            searchParams.get('search'),
        ],
        queryFn: ({ pageParam = null as string | null }) =>
            ListObjectWithPaginationV2(
                decodedPath ?? fullPath,
                pageParam ?? '',
                searchParams.get('search') ?? ''
            ),
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage: ListObjectWithPaginationResponse) =>
            lastPage?.nextContinuationToken ?? null,

        enabled: !!fullPath,
    })

    const folders = (data as any)?.pages?.flatMap(
        (page: any) => page?.folders || []
    )
    const files = (data as any)?.pages?.flatMap(
        (page: any) => page?.files || []
    )

    if (isLoading || isPermissionLoading)
        return <Loading.CircularProgress width={20} height={20} />

    if (!folders?.length && !files?.length)
        return (
            <div className="flex flex-col gap-4">
                <Header folderName={title} fullPath={fullPath} />
                <div className="flex flex-col gap-4 h-[60vh]">
                    <div className="w-full py-3 flex flex-col gap-1 bg-gray-50 items-center">
                        <p className="text-center text-gray-400 font-bold">
                            {searchParams.get('search') ? (
                                <span>
                                    {t('drive.not-found')}
                                    {` `}
                                    <span className="font-bold">
                                        {`"${searchParams.get('search')}"`}
                                    </span>
                                </span>
                            ) : (
                                t('empty-folder')
                            )}
                        </p>
                    </div>
                </div>
            </div>
        )

    return (
        <div className="flex flex-col gap-4">
            <Header folderName={title} fullPath={fullPath} />

            {isError && <p>{t('drive.error-loading-data')}</p>}

            <FileGrid folders={folders} files={files} />

            {hasNextPage && (
                <div className="flex justify-center py-4">
                    <button
                        className="px-4 py-2 bg-primary text-white rounded disabled:opacity-50"
                        onClick={() => {
                            fetchNextPage()
                        }}
                        disabled={isFetchingNextPage}
                    >
                        {isFetchingNextPage ? t('loading') : t('load-more')}
                    </button>
                </div>
            )}
        </div>
    )
}

export default FolderExplorer
